import React from 'react';
import { Link } from 'react-router-dom';
import { Smartphone, Layers, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ASSOC, LOAD, slotHref, deviceHref } from '../lib/monitorAssoc';

/*
 * The two lines under a Monitor tile: the NecroMancer device row this phone is ("device8"), and the
 * slots that run on it. The join itself is lib/monitorAssoc.js; this only draws its answer.
 *
 * Every state says what is actually known. "no device row" appears only once /uiapi/devices has
 * answered; a failed or pending fetch says so instead, because "we could not look" and "there is
 * none" lead the operator to different actions.
 *
 * The tile itself opens the expand view on click, so every link here stops the click from bubbling:
 * following a slot link must not also pop the HD dialog open behind the navigation.
 */

const stop = (e) => e.stopPropagation();

// A tooltip is not a place for 10,100 names.
const TOOLTIP_MAX = 40;
function listTitle(prefix, names) {
  const shown = names.slice(0, TOOLTIP_MAX);
  const more = names.length - shown.length;
  return `${prefix}${shown.join(', ')}${more > 0 ? `, and ${more} more` : ''}`;
}

function describeDevice(d) {
  const extra = [d.label, d.name && d.name !== d.label ? d.name : null].filter(Boolean);
  return extra.length ? `${d.id} · ${extra.join(' · ')}` : d.id;
}

function describeSlot(s) {
  return [s.name, s.username ? `@${s.username}` : null, s.label].filter(Boolean).join(' · ');
}

const Pulse = ({ label }) => (
  <span className="inline-block h-2.5 w-14 animate-pulse rounded-full bg-secondary/70" role="status" aria-label={label} />
);

const Muted = ({ children, title, testId }) => (
  <span className="min-w-0 truncate italic text-muted-foreground/70" title={title} data-testid={testId}>{children}</span>
);

export default function MonitorPhoneAssoc({ assoc, cap = 3, align = 'center', className }) {
  const state = assoc ? assoc.state : ASSOC.LOADING;
  const devices = (assoc && assoc.devices) || [];
  const slots = (assoc && assoc.slots) || [];
  const slotsState = assoc ? assoc.slotsState : LOAD.LOADING;
  const otherPcs = (assoc && assoc.otherPcs) || [];
  const line = cn('flex min-w-0 items-center gap-1 leading-4', align === 'start' ? 'justify-start' : 'justify-center');
  const icon = 'h-3 w-3 shrink-0 text-muted-foreground/70';

  // One warning mark, carrying whichever of the two caveats apply.
  const caveats = [];
  if (state === ASSOC.AMBIGUOUS) {
    caveats.push(`${devices.length} device rows use this serial as their UDID and this PC does not single one out`);
  }
  if (otherPcs.length > 0) {
    caveats.push(`This serial is also reported by ${otherPcs.join(', ')}`);
  }

  let deviceContent;
  if (state === ASSOC.LOADING) {
    deviceContent = <Pulse label="Loading device" />;
  } else if (state === ASSOC.UNAVAILABLE) {
    deviceContent = (
      <Muted testId="assoc-device-unavailable" title="The device list did not load. Press Refresh to try again.">
        device info unavailable
      </Muted>
    );
  } else if (state === ASSOC.NONE) {
    deviceContent = (
      <Muted testId="assoc-no-device" title="No device on this account has this serial as its UDID. Add it on the Devices page.">
        no device row
      </Muted>
    );
  } else {
    deviceContent = (
      <span className="min-w-0 truncate" data-testid={state === ASSOC.AMBIGUOUS ? 'assoc-ambiguous' : undefined}>
        {devices.map((d, i) => (
          <React.Fragment key={d.id}>
            {i > 0 && <span className="text-muted-foreground"> / </span>}
            <Link
              to={deviceHref(d.id)}
              onClick={stop}
              title={describeDevice(d)}
              className="font-medium text-foreground/90 hover:text-primary hover:underline"
              data-testid="assoc-device"
            >
              {d.id}
            </Link>
          </React.Fragment>
        ))}
      </span>
    );
  }

  let slotsContent = null;
  if (state === ASSOC.MATCHED || state === ASSOC.AMBIGUOUS) {
    if (slotsState === LOAD.LOADING) {
      slotsContent = <Pulse label="Loading slots" />;
    } else if (slotsState === LOAD.ERROR) {
      slotsContent = (
        <Muted testId="assoc-slots-unavailable" title="The slot list did not load. Press Refresh to try again.">
          slots unavailable
        </Muted>
      );
    } else if (slots.length === 0) {
      slotsContent = (
        <Muted testId="assoc-no-slot" title="No slot on this account is assigned to this device.">
          no slot
        </Muted>
      );
    } else {
      const n = Math.max(1, cap);
      const shown = slots.slice(0, n);
      const rest = slots.slice(n);
      slotsContent = (
        <>
          <span className="min-w-0 truncate" title={listTitle('', slots.map((s) => s.name))}>
            {shown.map((s, i) => (
              <React.Fragment key={s.name}>
                {i > 0 && <span className="text-muted-foreground">, </span>}
                <Link
                  to={slotHref(s.name)}
                  onClick={stop}
                  title={describeSlot(s)}
                  className="text-primary hover:underline"
                  data-testid="assoc-slot-link"
                >
                  {s.name}
                </Link>
              </React.Fragment>
            ))}
          </span>
          {rest.length > 0 && (
            <span
              className="shrink-0 tabular-nums text-muted-foreground"
              title={listTitle('Also: ', rest.map((s) => s.name))}
              data-testid="assoc-more"
            >
              +{rest.length}
            </span>
          )}
        </>
      );
    }
  }

  return (
    <div className={cn('min-w-0 text-[11px]', className)} data-testid="phone-assoc" data-assoc-state={state}>
      <p className={line}>
        <Smartphone className={icon} aria-hidden="true" />
        <span className="sr-only">Device: </span>
        {deviceContent}
        {caveats.length > 0 && (
          <AlertTriangle
            className="h-3 w-3 shrink-0 text-warning"
            aria-label={caveats.join('. ')}
            role="img"
            data-testid="assoc-caveat"
          >
            <title>{caveats.join('. ')}</title>
          </AlertTriangle>
        )}
      </p>
      {slotsContent && (
        <p className={line} data-testid="assoc-slots">
          <Layers className={icon} aria-hidden="true" />
          <span className="sr-only">Slots: </span>
          {slotsContent}
        </p>
      )}
    </div>
  );
}
